/* Ask DSP theme bridge. No secrets, model calls, or automatic cart mutations. */
(() => {
  'use strict';
  if (window.__dspAssistantLoaded) return;
  window.__dspAssistantLoaded = true;
  const APP_ORIGIN = 'https://dsp-parts-assistant.vercel.app';
  const allowedParents = new Set(['https://dronespraypro.com', 'https://www.dronespraypro.com', 'https://xxmvbf-4t.myshopify.com']);
  // Shopify visitor previews use a temporary hostname instead of the store domain.
  // Admit only this store's preview host, never previews belonging to other stores.
  if (/^https:\/\/[a-z0-9]{1,51}-91148943729\.shopifypreview\.com$/.test(window.location.origin)) {
    allowedParents.add(window.location.origin);
  }
  if (!allowedParents.has(window.location.origin)) return;
  let dialog;
  let frame;
  let trigger;
  let frameReady = false;
  let pendingOpen = null;
  let hostSearchForm;
  let hostSearchInput;
  let hostSearchComposing = false;
  let pendingSearchModel = false;
  let readyTimer;
  let scrollState;
  let status;
  let heading;
  let modeSwitch;
  let viewportUpdate;
  let viewportWasReduced = false;
  let viewportDismissed = false;
  let viewportFocusPending = false;
  let viewportFocusTimer;
  let inputFocused = false;
  let viewportDismissTimer;
  let viewportSettleTimers = [];
  let fullPageLink;
  let cartHandoff = false;
  let lastPopupQuery = '';
  let resizeHandle;
  let resizeToggle;
  let restoreChatHeight = null;
  let resizeGesture = null;
  let preferredChatHeight = null;
  try {
    const saved = Number(sessionStorage.getItem('dsp-chat-height'));
    if (Number.isFinite(saved) && saved >= 300 && saved <= 10000) preferredChatHeight = saved;
    const savedRestore = Number(sessionStorage.getItem('dsp-chat-restore-height'));
    if (Number.isFinite(savedRestore) && savedRestore >= 300 && savedRestore <= 10000) restoreChatHeight = savedRestore;
  } catch { /* Resizing also works when browser storage is unavailable. */ }
  const readyInlineFrames = new WeakSet();
  const pendingInlineOpens = new WeakMap();
  let cartBusy = false;
  let activeRequestId = null;
  const completedRequests = new Map();
  // Keep an uncertain write locked for this storefront page, even if the iframe reloads.
  let unresolvedCart = null;

  let privacyLoading = false;
  let privacyKnown = false;
  function analyticsAllowed() {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true; }
    catch { return false; }
  }
  function sendAnalyticsConsent(element) {
    // Loading is unknown, not an explicit denial; the child already defaults off.
    const readable = typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function';
    if (!readable && !privacyKnown) return;
    if (readable) privacyKnown = true;
    try {
      if (new URL(element.src).origin === APP_ORIGIN) element.contentWindow?.postMessage({ type: 'dsp:analytics-consent', allowed: analyticsAllowed(), device: window.innerWidth < 768 ? 'mobile' : 'desktop' }, APP_ORIGIN);
    } catch { /* Untrusted or unavailable frame. */ }
  }
  function broadcastAnalyticsConsent() {
    document.querySelectorAll('iframe[data-dsp-iframe]').forEach(sendAnalyticsConsent);
  }
  function loadPrivacy() {
    broadcastAnalyticsConsent();
    if (window.Shopify?.customerPrivacy || privacyLoading || typeof window.Shopify?.loadFeatures !== 'function') return;
    privacyLoading = true;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
        privacyLoading = false;
        if (!error) broadcastAnalyticsConsent();
      });
    } catch { privacyLoading = false; }
  }
  // Read merchant consent policy; never change consent, attribution or cookies.
  document.addEventListener('visitorConsentCollected', broadcastAnalyticsConsent);
  loadPrivacy();

  function productDestination(value) {
    if (typeof value !== 'string' || value.length > 2048) return null;
    try {
      const url = new URL(value);
      if (!allowedParents.has(url.origin) || url.username || url.password) return null;
      const product = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:collections\/[a-z0-9_-]+\/)?products\/([a-z0-9][a-z0-9_-]*)\/?$/i.exec(url.pathname);
      if (!product) return null;
      const configuredRoot = window.Shopify?.routes?.root;
      const locale = typeof configuredRoot === 'string' && /^\/[a-z]{2}(?:-[a-z]{2})?\/$/i.test(configuredRoot) ? configuredRoot : '/';
      const destination = new URL(`${locale}products/${product[1]}`, window.location.origin);
      // Preserve the original product link's options and campaign/referral data.
      // Conversation state remains in session storage, never added to the URL.
      destination.search = url.search;
      const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (/^\d{1,20}$/.test(previewTheme || '')) destination.searchParams.set('preview_theme_id', previewTheme);
      destination.hash = url.hash;
      return destination.href;
    } catch { return null; }
  }

  function rootPath() {
    const root = window.Shopify?.routes?.root || '/';
    return typeof root === 'string' && /^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?$/.test(root) ? root : '/';
  }
  function plainQuantity(cart, id) {
    return cart.items.filter(line => String(line.variant_id || line.id) === id &&
      line.selling_plan_allocation == null && line.selling_plan == null &&
      (line.properties == null || (typeof line.properties === 'object' && !Array.isArray(line.properties) &&
        Object.values(line.properties).every(value => value == null || value === ''))))
      .reduce((quantity, line) => {
        if (!Number.isInteger(line.quantity) || line.quantity < 0) throw new Error('Invalid cart quantity');
        return quantity + line.quantity;
      }, 0);
  }
  async function readCart() {
    const response = await fetch(`${rootPath()}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(10000) });
    if (!response.ok) throw new Error('Cart read failed');
    const cart = await response.json();
    if (!Array.isArray(cart.items) || cart.items.some(line => !line || typeof line !== 'object')) throw new Error('Cart could not be verified');
    return cart;
  }
  function rootElement() { return document.querySelector('[data-dsp-assistant-root]'); }
  function enhanceHeaderSearch() {
    const root = rootElement();
    if (root?.dataset.enableSearch !== 'true') return;
    let triggers;
    try { triggers = document.querySelectorAll(root.dataset.searchSelector || 'details-modal.header__search summary.header__icon--search'); }
    catch { return; }
    triggers.forEach(element => {
      if (!(element instanceof HTMLElement) || element.dataset.dspHeaderEnhanced === 'true') return;
      const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('viewBox', '0 0 24 24');
      icon.setAttribute('width', '22');
      icon.setAttribute('height', '22');
      icon.setAttribute('fill', 'none');
      icon.setAttribute('stroke', 'currentColor');
      icon.setAttribute('stroke-width', '1.8');
      icon.setAttribute('stroke-linecap', 'round');
      icon.setAttribute('aria-hidden', 'true');
      icon.classList.add('dsp-header-search-icon');
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', '10.5'); circle.setAttribute('cy', '10.5'); circle.setAttribute('r', '6.5');
      const handle = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      handle.setAttribute('d', 'm16 16 4.5 4.5');
      icon.append(circle, handle);
      const label = document.createElement('span');
      label.className = 'dsp-header-search-label';
      label.textContent = 'Search';
      element.replaceChildren(icon, label);
      element.classList.add('dsp-header-search');
      element.setAttribute('aria-label', 'Search');
      element.setAttribute('aria-haspopup', 'dialog');
      element.dataset.dspHeaderEnhanced = 'true';
    });
  }
  function inlineFrame() {
    return [...document.querySelectorAll('iframe[data-dsp-iframe][data-dsp-inline]')].find(element => {
      try { return new URL(element.src).origin === APP_ORIGIN; }
      catch { return false; }
    });
  }
  function sendInlineOpen(element) {
    const command = pendingInlineOpens.get(element);
    if (!command || !readyInlineFrames.has(element)) return;
    sendAnalyticsConsent(element);
    element.contentWindow?.postMessage(command, APP_ORIGIN);
    pendingInlineOpens.delete(element);
  }
  function requestReady(element) {
    element.contentWindow?.postMessage({ type: 'dsp:ready-request' }, APP_ORIGIN);
  }
  function productGid(value) { return /^gid:\/\/shopify\/Product\/\d{1,20}$/.test(value || '') ? value : null; }
  function searchQuery(value) { return typeof value === 'string' ? value.trim().slice(0, 300) : undefined; }
  function storeSearchUrl(query) {
    // Build only a store search URL. Never rewrite the page URL or attribution parameters.
    const url = new URL(`${rootPath()}search`, window.location.origin);
    if (query) url.searchParams.set('q', query);
    const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (/^\d{1,20}$/.test(previewTheme || '')) url.searchParams.set('preview_theme_id', previewTheme);
    return url;
  }
  function updateFullPageLink() {
    if (!fullPageLink) return;
    const chat = dialog?.dataset.dspMode === 'chat';
    const url = storeSearchUrl(lastPopupQuery);
    if (chat) url.searchParams.set('dsp_mode', 'chat');
    fullPageLink.textContent = chat ? 'Open full chat page' : 'Open full search page';
    fullPageLink.href = url.href;
  }
  async function closeForCart() {
    if (!dialog?.open) return;
    cartHandoff = true;
    await new Promise(resolve => {
      dialog.addEventListener('close', resolve, { once: true });
      dialog.close();
    });
    cartHandoff = false;
  }
  function showCartConfirmation() {
    document.querySelector('.dsp-cart-confirmation')?.remove();
    const confirmation = document.createElement('div');
    confirmation.className = 'dsp-cart-confirmation';
    confirmation.setAttribute('role', 'status');
    const message = document.createElement('span');
    message.textContent = 'Added to your cart.';
    const link = document.createElement('a');
    link.href = `${rootPath()}cart`;
    link.textContent = 'View cart';
    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.textContent = 'Close';
    dismiss.setAttribute('aria-label', 'Dismiss cart confirmation');
    dismiss.addEventListener('click', () => confirmation.remove());
    confirmation.append(message, link, dismiss);
    document.body.append(confirmation);
  }
  async function presentStoreCart(sourceFrame, requestId) {
    // The confirmed write is complete. Presentation failure must never cause a
    // retry or turn success into an uncertain cart mutation.
    await closeForCart();
    document.querySelector('.dsp-cart-confirmation')?.remove();
    if (typeof window.upcartRefreshCart === 'function' && typeof window.upcartOpenCart === 'function') {
      let timeout;
      try {
        const notification = document.querySelector('cart-notification');
        if (notification?.querySelector('.cart-notification.active') && typeof notification.close === 'function') notification.close();
        await Promise.race([
          Promise.resolve(window.upcartRefreshCart()),
          new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error('Cart drawer refresh timed out')), 5000); }),
        ]);
        window.upcartOpenCart();
        if (analyticsAllowed()) sourceFrame.contentWindow?.postMessage({ type: 'dsp:cart-drawer-opened', requestId }, APP_ORIGIN);
        return;
      } catch { /* Keep success and offer an explicit cart link if the drawer fails. */ }
      finally { clearTimeout(timeout); }
    }
    showCartConfirmation();
  }
  function explicitQuery(element) {
    if (element.hasAttribute('data-dsp-query')) return searchQuery(element.dataset.dspQuery);
    const form = element instanceof HTMLFormElement ? element : element.closest('form');
    const input = form?.querySelector('[name="q"]');
    if (input && 'value' in input) return searchQuery(input.value);
    if (element instanceof HTMLAnchorElement) {
      const url = new URL(element.href);
      if (url.origin === window.location.origin && url.pathname === `${rootPath()}search` && url.searchParams.has('q')) return searchQuery(url.searchParams.get('q'));
    }
    return undefined;
  }
  function chatHeightBounds() {
    // Reserve room above the window for its protruding tab and pointer target.
    const max = Math.max(1, window.innerHeight - 56);
    return { min: Math.min(560, max), max };
  }
  function applyChatHeight(height = preferredChatHeight ?? 820, remember = false) {
    const { min, max } = chatHeightBounds();
    const value = Math.round(Math.max(min, Math.min(max, height)));
    dialog.style.setProperty('--dsp-chat-height', `${value}px`);
    resizeHandle.setAttribute('aria-valuemin', String(Math.round(min)));
    resizeHandle.setAttribute('aria-valuemax', String(Math.round(max)));
    resizeHandle.setAttribute('aria-valuenow', String(value));
    resizeHandle.setAttribute('aria-valuetext', `${value} pixels tall`);
    if (resizeToggle) {
      const fullHeight = value >= max - 1;
      resizeToggle.textContent = min === max ? 'Full height' : fullHeight ? 'Restore' : 'Expand';
      resizeToggle.disabled = min === max;
      resizeToggle.setAttribute('aria-label', min === max ? 'Chat already uses the available screen height' : fullHeight ? 'Restore chat height' : 'Expand chat to full height');
      resizeToggle.title = min === max ? 'Chat is already as tall as this window allows.' : fullHeight ? 'Return to a shorter chat window' : 'Use the full available window height';
    }
    if (remember) {
      preferredChatHeight = value;
      try { sessionStorage.setItem('dsp-chat-height', String(value)); } catch { /* Optional session preference. */ }
    }
  }
  function finishResize() {
    if (!resizeGesture) return;
    const pointerId = resizeGesture.pointerId;
    resizeGesture = null;
    dialog.classList.remove('dsp-is-resizing');
    if (resizeHandle.hasPointerCapture(pointerId)) resizeHandle.releasePointerCapture(pointerId);
  }
  function createResizeHandle() {
    resizeHandle = document.createElement('div');
    resizeHandle.className = 'dsp-chat-resize';
    // A real child keeps Dawn's div:empty rule from hiding the grip.
    const grip = document.createElement('span');
    grip.className = 'dsp-chat-grip';
    grip.setAttribute('aria-hidden', 'true');
    // Fill through the window border, then stroke only the raised outline.
    // This removes the horizontal seam without drawing a bottom edge on the tab.
    grip.innerHTML = '<svg class="dsp-chat-tab-outline" viewBox="0 0 112 14" aria-hidden="true" focusable="false"><path d="M0 13 C8 13 10 11 10 8 C10 4 14 1 20 1 H92 C98 1 102 4 102 8 C102 11 104 13 112 13 V14 H0 Z" fill="#fff"/><path d="M0 13 C8 13 10 11 10 8 C10 4 14 1 20 1 H92 C98 1 102 4 102 8 C102 11 104 13 112 13" fill="none" stroke="#d0ddc5"/></svg>';
    resizeHandle.append(grip);
    resizeHandle.tabIndex = 0;
    resizeHandle.setAttribute('role', 'separator');
    resizeHandle.setAttribute('aria-orientation', 'horizontal');
    resizeHandle.setAttribute('aria-label', 'Resize chat height');
    resizeHandle.setAttribute('aria-controls', 'dsp-assistant-frame');
    resizeHandle.title = 'Drag up to make chat taller. Use Up and Down arrow keys to resize.';
    resizeHandle.addEventListener('pointerdown', event => {
      if (event.button !== 0 || dialog.dataset.dspMode !== 'chat' || window.matchMedia('(max-width: 749px), (hover: none) and (pointer: coarse) and (max-width: 1024px)').matches) return;
      event.preventDefault();
      resizeGesture = { pointerId: event.pointerId, y: event.clientY, height: dialog.getBoundingClientRect().height };
      resizeHandle.focus({ preventScroll: true });
      resizeHandle.setPointerCapture(event.pointerId);
      dialog.classList.add('dsp-is-resizing');
    });
    resizeHandle.addEventListener('pointermove', event => {
      if (!resizeGesture || event.pointerId !== resizeGesture.pointerId) return;
      applyChatHeight(resizeGesture.height + resizeGesture.y - event.clientY, true);
    });
    for (const eventName of ['pointerup', 'pointercancel', 'lostpointercapture']) resizeHandle.addEventListener(eventName, finishResize);
    resizeHandle.addEventListener('keydown', event => {
      const { min, max } = chatHeightBounds();
      const current = dialog.getBoundingClientRect().height;
      const heights = { ArrowUp: current + 32, ArrowDown: current - 32, Home: min, End: max };
      if (!(event.key in heights)) return;
      event.preventDefault();
      applyChatHeight(heights[event.key], true);
    });
    window.addEventListener('resize', () => { finishResize(); applyChatHeight(); });
    applyChatHeight();
    return resizeHandle;
  }
  function setMode(mode) {
    finishResize();
    dialog.dataset.dspMode = mode;
    heading.setAttribute('aria-label', mode === 'search' ? 'Ask DSP product search' : 'Ask DSP chat');
    const icon = mode === 'chat'
      ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/></svg>'
      : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 4h16v12H8l-4 4V4Z"/></svg>';
    modeSwitch.innerHTML = `${icon}<span>${mode === 'chat' ? 'Search' : 'Chat'}</span>`;
    modeSwitch.setAttribute('aria-label', mode === 'chat' ? 'Switch to Explore products' : 'Switch to Ask DSP chat');
    updateFullPageLink();
  }
  // iOS keeps the layout viewport tall while its keyboard shrinks/pans the
  // visual viewport. Size the entire modal, not just the child composer, to the
  // visible rectangle so the host controls and iframe stay in the same plane.
  function syncMobileViewport() {
    if (!dialog?.open) return;
    const mobile = window.matchMedia('(max-width: 749px), (hover: none) and (pointer: coarse) and (max-width: 1024px)').matches;
    const viewport = window.visualViewport;
    if (mobile && viewport) {
      const layoutHeight = document.documentElement.clientHeight || window.innerHeight;
      // A focus message can precede Safari's next viewport event. Keep the
      // recovered surface until that event instead of replaying stale keyboard
      // bounds for a frame when the shopper taps the composer again.
      const height = viewportDismissed || viewportFocusPending ? layoutHeight : Math.min(layoutHeight, Math.max(1, Math.round(viewport.height)));
      const reduced = layoutHeight - height > 120;
      // Safari can report the old keyboard pan after its height has recovered.
      // Never carry that offset into the full-height popup or outside the screen.
      const top = reduced ? Math.max(0, Math.min(Math.round(viewport.offsetTop), layoutHeight - height)) : 0;
      dialog.style.setProperty('--dsp-visible-height', `${height}px`);
      dialog.style.setProperty('--dsp-visible-top', `${top}px`);
      if (viewportWasReduced && !reduced) {
        // Reset native root scrolling after keyboard animation, including its
        // touch-coordinate plane. The saved storefront scroll restores on close.
        if (window.scrollX || window.scrollY || document.documentElement.scrollTop || document.body.scrollTop) {
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }
        frame?.contentWindow?.postMessage({ type: 'dsp:viewport-restored' }, APP_ORIGIN);
      }
      viewportWasReduced = reduced;
    } else {
      dialog.style.removeProperty('--dsp-visible-height');
      dialog.style.removeProperty('--dsp-visible-top');
    }
  }
  function scheduleViewportSync(event) {
    if (event && (event.type === 'resize' || event.type === 'scroll')) {
      viewportFocusPending = false;
      window.clearTimeout(viewportFocusTimer);
    }
    if (viewportUpdate) window.cancelAnimationFrame(viewportUpdate);
    viewportUpdate = window.requestAnimationFrame(() => {
      viewportUpdate = null;
      syncMobileViewport();
    });
  }
  function settleMobileViewport() {
    viewportSettleTimers.forEach(window.clearTimeout);
    // iOS focus/blur and visual-viewport resize arrive in different orders.
    // Sample after the animation as well as on the next paint; no input refocus.
    scheduleViewportSync();
    viewportSettleTimers = [100, 350, 700].map(delay => window.setTimeout(scheduleViewportSync, delay));
  }
  function setLoading() {
    window.clearTimeout(readyTimer);
    status.textContent = 'Loading Ask DSP…';
    readyTimer = window.setTimeout(() => {
      if (!frameReady) status.textContent = 'Taking longer than expected. Try opening the full search page below.';
    }, 12000);
  }
  function rememberStyle(element, properties) {
    return properties.map(name => [name, element.style.getPropertyValue(name), element.style.getPropertyPriority(name)]);
  }
  function restoreStyle(element, properties) {
    for (const [name, value, priority] of properties) {
      if (value) element.style.setProperty(name, value, priority);
      else element.style.removeProperty(name);
    }
  }
  function lockScroll() {
    const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    scrollState = {
      x: window.scrollX, y: window.scrollY,
      html: rememberStyle(document.documentElement, ['overflow', 'scroll-behavior']),
      body: rememberStyle(document.body, ['position', 'top', 'left', 'width', 'overflow', 'padding-right']),
    };
    const padding = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
    document.documentElement.style.setProperty('overflow', 'hidden');
    document.body.style.setProperty('position', 'fixed');
    document.body.style.setProperty('top', `${-scrollState.y}px`);
    document.body.style.setProperty('left', `${-scrollState.x}px`);
    document.body.style.setProperty('width', '100%');
    document.body.style.setProperty('overflow', 'hidden');
    if (scrollbar) document.body.style.setProperty('padding-right', `${padding + scrollbar}px`);
  }
  function unlockScroll() {
    if (!scrollState) return;
    const saved = scrollState;
    scrollState = null;
    restoreStyle(document.body, saved.body);
    restoreStyle(document.documentElement, saved.html);
    document.documentElement.style.setProperty('scroll-behavior', 'auto');
    window.scrollTo(saved.x, saved.y);
    restoreStyle(document.documentElement, saved.html);
  }
  function sendOpen() {
    // A late ready signal must not focus a composer inside an already-closed popup.
    if (!frameReady || !pendingOpen || !dialog?.open) return;
    sendAnalyticsConsent(frame);
    frame.contentWindow?.postMessage(pendingOpen, APP_ORIGIN);
    pendingOpen = null;
    if (dialog.dataset.hostSearch === 'true') {
      if (hostSearchComposing) sendSearchInput('input', true);
      if (document.activeElement === hostSearchInput) sendSearchInput('focus');
      if (pendingSearchModel) { pendingSearchModel = false; sendSearchInput('model'); }
    }
  }
  function usesMobileSearch() {
    return window.matchMedia('(max-width: 749px), (hover: none) and (pointer: coarse) and (max-width: 1024px)').matches;
  }
  function updateInputFocus(focused) {
    if (focused && !inputFocused && viewportDismissed) viewportFocusPending = true;
    if (!focused) viewportFocusPending = false;
    inputFocused = focused;
    window.clearTimeout(viewportFocusTimer);
    if (viewportFocusPending) viewportFocusTimer = window.setTimeout(() => {
      // A viewport event can precede the iframe focus message, or be omitted
      // when Safari retains the same geometry. Never wait indefinitely.
      if (!inputFocused || !dialog?.open) return;
      viewportFocusPending = false;
      scheduleViewportSync();
    }, 350);
    window.clearTimeout(viewportDismissTimer);
    if (focused) viewportDismissed = false;
    else viewportDismissTimer = window.setTimeout(() => {
      if (inputFocused || !dialog?.open) return;
      viewportDismissed = true;
      scheduleViewportSync();
    }, 700);
    settleMobileViewport();
  }
  function sendSearchInput(action, composing = false) {
    if (!dialog?.open || dialog.dataset.hostSearch !== 'true') return;
    const query = hostSearchInput.value.slice(0, 300);
    lastPopupQuery = query;
    updateFullPageLink();
    if (frameReady) {
      frame.contentWindow?.postMessage({ type: 'dsp:search-input', action, query, composing }, APP_ORIGIN);
    } else {
      if (pendingOpen && !composing) pendingOpen.query = query;
      if (action === 'model') pendingSearchModel = true;
    }
  }
  function createHostSearch() {
    // This is the actual mobile search field, not a disposable focus proxy.
    // Safari opens its keyboard during the storefront tap and keeps the same
    // focused node while the cross-origin app loads and returns results.
    hostSearchForm = document.createElement('form');
    hostSearchForm.className = 'dsp-host-search';
    hostSearchForm.setAttribute('role', 'search');
    const field = document.createElement('div');
    field.className = 'dsp-host-search-field';
    hostSearchInput = document.createElement('input');
    hostSearchInput.type = 'search';
    hostSearchInput.maxLength = 300;
    hostSearchInput.enterKeyHint = 'search';
    hostSearchInput.setAttribute('aria-label', 'Search products');
    hostSearchInput.placeholder = 'Search drones, batteries, parts…';
    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'dsp-host-search-clear';
    clear.setAttribute('aria-label', 'Clear search');
    clear.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6"/></svg>';
    clear.hidden = true;
    clear.addEventListener('click', () => {
      hostSearchInput.blur();
      hostSearchInput.value = '';
      clear.hidden = true;
      sendSearchInput('reset');
    });
    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'dsp-host-search-submit';
    submit.setAttribute('aria-label', 'Search');
    submit.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 12h16m-6-6 6 6-6 6"/></svg>';
    const model = document.createElement('button');
    model.type = 'button';
    model.className = 'dsp-host-search-model';
    model.setAttribute('aria-label', 'Filter by drone model');
    model.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 7h16M4 17h16M8 4v6m8 4v6"/></svg><span>Model</span>';
    model.addEventListener('click', () => {
      hostSearchInput.blur();
      sendSearchInput('model');
    });
    field.append(hostSearchInput, clear, submit);
    hostSearchForm.append(field, model);
    hostSearchInput.addEventListener('focus', () => {
      updateInputFocus(true);
      sendSearchInput('focus');
    });
    hostSearchInput.addEventListener('blur', () => {
      updateInputFocus(false);
      sendSearchInput('blur');
    });
    hostSearchInput.addEventListener('input', () => {
      clear.hidden = !hostSearchInput.value;
      sendSearchInput('input', hostSearchComposing);
    });
    hostSearchInput.addEventListener('compositionstart', () => {
      hostSearchComposing = true;
      sendSearchInput('input', true);
    });
    hostSearchInput.addEventListener('compositionend', () => {
      hostSearchComposing = false;
      sendSearchInput('input');
    });
    hostSearchForm.addEventListener('submit', event => {
      event.preventDefault();
      if (hostSearchComposing || !hostSearchInput.value.trim()) return;
      sendSearchInput('submit');
      hostSearchInput.blur();
    });
    return hostSearchForm;
  }
  function focusMobileSearch(command) {
    const active = command.mode === 'search' && usesMobileSearch();
    if (active) command.hostSearch = true;
    else delete command.hostSearch;
    dialog.dataset.hostSearch = String(active);
    if (!active) return false;
    hostSearchInput.value = command.query ?? lastPopupQuery;
    command.query = hostSearchInput.value;
    hostSearchForm.querySelector('.dsp-host-search-clear').hidden = !hostSearchInput.value;
    hostSearchInput.focus({ preventScroll: true });
    updateInputFocus(true);
    return true;
  }
  function createDialog(command) {
    dialog = document.createElement('dialog');
    dialog.className = 'dsp-assistant-dialog';
    dialog.setAttribute('aria-labelledby', 'dsp-assistant-dialog-heading');
    const header = document.createElement('div');
    header.className = 'dsp-embed-header';
    heading = document.createElement('h2');
    heading.id = 'dsp-assistant-dialog-heading';
    heading.className = 'dsp-embed-heading';
    heading.tabIndex = -1;
    const logo = document.createElement('img');
    logo.src = `${APP_ORIGIN}/brand/logo-icon.png`;
    logo.alt = '';
    logo.width = 28;
    logo.height = 24;
    const brandLabel = document.createElement('span');
    brandLabel.textContent = 'Ask DSP';
    heading.append(logo, brandLabel);
    modeSwitch = document.createElement('button');
    modeSwitch.type = 'button';
    modeSwitch.className = 'dsp-embed-mode-switch';
    modeSwitch.addEventListener('click', () => {
      const mode = dialog.dataset.dspMode === 'chat' ? 'search' : 'chat';
      setMode(mode);
      if (mode === 'search') {
        const next = { type: 'dsp:open', mode, productId: null, currentProductId: productGid(rootElement()?.dataset.currentProductId) };
        pendingOpen = next;
        focusMobileSearch(next);
        sendOpen();
      } else {
        hostSearchInput.blur();
        dialog.dataset.hostSearch = 'false';
        if (frameReady) frame.contentWindow?.postMessage({ type: 'dsp:set-mode', mode }, APP_ORIGIN);
        else if (pendingOpen) { pendingOpen.mode = mode; pendingOpen.hostSearch = false; }
      }
    });
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'dsp-embed-close';
    close.textContent = 'Close';
    close.setAttribute('aria-label', 'Close Ask DSP');
    close.addEventListener('click', () => dialog.close());
    resizeToggle = document.createElement('button');
    resizeToggle.type = 'button';
    resizeToggle.className = 'dsp-embed-expand';
    resizeToggle.addEventListener('click', () => {
      finishResize();
      const { min, max } = chatHeightBounds();
      const current = dialog.getBoundingClientRect().height;
      if (current >= max - 1) {
        // A remembered height can exceed a smaller viewport. Restore must still
        // produce a visibly shorter panel when there is room to resize.
        const shorter = restoreChatHeight != null && restoreChatHeight < max ? restoreChatHeight : Math.min(820, Math.max(min, max - 160));
        applyChatHeight(shorter, true);
      } else {
        restoreChatHeight = current;
        try { sessionStorage.setItem('dsp-chat-restore-height', String(current)); } catch { /* Optional session preference. */ }
        applyChatHeight(max, true);
      }
    });
    const actions = document.createElement('div');
    actions.className = 'dsp-embed-actions';
    actions.append(resizeToggle, close);
    header.append(heading, modeSwitch, createResizeHandle(), actions);
    frame = document.createElement('iframe');
    frame.id = 'dsp-assistant-frame';
    frame.title = 'Ask DSP shopping assistant';
    frame.dataset.dspIframe = '';
    frame.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox');
    frame.referrerPolicy = 'strict-origin-when-cross-origin';
    const url = new URL('/embed', APP_ORIGIN);
    url.searchParams.set('parent', window.location.origin);
    url.searchParams.set('mode', command.mode);
    url.searchParams.set('surface', 'dialog');
    url.searchParams.set('controls', 'host');
    url.searchParams.set('host_header', '1');
    url.searchParams.set('live_search', '1');
    if (command.productId) url.searchParams.set('product', command.productId);
    if (command.currentProductId) url.searchParams.set('current_product', command.currentProductId);
    // Popup queries are delivered once through dsp:open after the child is ready.
    // Inline search sections instead receive their initial query in the iframe URL.
    frame.src = url.href;
    const fallback = document.createElement('a');
    fallback.className = 'dsp-embed-fallback';
    fullPageLink = fallback;
    fallback.textContent = 'Open full search page';
    status = document.createElement('p');
    status.className = 'dsp-embed-status';
    status.setAttribute('role', 'status');
    const links = document.createElement('div');
    links.className = 'dsp-embed-links';
    links.append(fallback);
    const footer = document.createElement('div');
    footer.className = 'dsp-embed-footer';
    footer.append(status, links);
    dialog.append(header, createHostSearch(), frame, footer);
    document.body.append(dialog);
    window.visualViewport?.addEventListener('resize', scheduleViewportSync);
    window.visualViewport?.addEventListener('scroll', scheduleViewportSync);
    window.addEventListener('resize', scheduleViewportSync);
    dialog.addEventListener('click', event => {
      const bounds = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) dialog.close();
    });
    dialog.addEventListener('close', () => {
      hostSearchInput.blur();
      hostSearchComposing = false;
      pendingSearchModel = false;
      pendingOpen = null;
      frame.contentWindow?.postMessage({ type: 'dsp:host-visibility', visible: false }, APP_ORIGIN);
      finishResize();
      viewportSettleTimers.forEach(window.clearTimeout);
      viewportSettleTimers = [];
      viewportWasReduced = false;
      viewportDismissed = false;
      viewportFocusPending = false;
      window.clearTimeout(viewportFocusTimer);
      inputFocused = false;
      window.clearTimeout(viewportDismissTimer);
      if (viewportUpdate) window.cancelAnimationFrame(viewportUpdate);
      unlockScroll();
      if (!cartHandoff && trigger?.isConnected) trigger.focus({ preventScroll: true });
    });
    setLoading();
  }
  function openAssistant(element, requestedMode, returnFocusElement = element) {
    trigger = returnFocusElement;
    const mode = requestedMode === 'search' || element.dataset.dspMode === 'search' ? 'search' : 'chat';
    const command = {
      type: 'dsp:open', mode,
      productId: productGid(element.dataset.productId),
      currentProductId: productGid(rootElement()?.dataset.currentProductId),
    };
    const query = mode === 'search' ? explicitQuery(element) : undefined;
    if (query !== undefined) command.query = query;
    const inline = inlineFrame();
    if (inline) {
      // Search-page tabs already offer chat. Reuse their search and conversation
      // state rather than creating a second app instance on this page.
      rootElement()?.classList.add('dsp-has-inline');
      pendingInlineOpens.set(inline, command);
      inline.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      if (readyInlineFrames.has(inline)) sendInlineOpen(inline);
      else requestReady(inline);
      return;
    }
    pendingOpen = command;
    if (!dialog) createDialog(command);
    setMode(mode);
    if (query !== undefined) lastPopupQuery = query;
    updateFullPageLink();
    if (!dialog.open) {
      lockScroll();
      try { dialog.showModal(); }
      catch { unlockScroll(); window.location.assign(fullPageLink.href); return; }
      syncMobileViewport();
      if (!focusMobileSearch(command)) {
        // Keep initial mobile chat focus inside the dialog without selecting
        // a control or opening the keyboard. Tab still reaches every action.
        const initialFocus = mode === 'chat' && usesMobileSearch() ? heading : dialog.querySelector('.dsp-embed-close');
        initialFocus.focus({ preventScroll: true });
      }
    } else focusMobileSearch(command);
    sendOpen();
  }
  function dawnSearchTrigger(target) {
    const root = rootElement();
    if (root?.dataset.enableSearch !== 'true') return null;
    try { return target.closest(root.dataset.searchSelector || 'details-modal.header__search summary.header__icon--search'); }
    catch { return null; }
  }
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !(event.target instanceof Element)) return;
    const explicit = event.target.closest('[data-dsp-open]:not(form)');
    const dawn = dawnSearchTrigger(event.target);
    const button = explicit || dawn;
    if (!button || button.matches(':disabled') || (button instanceof HTMLAnchorElement && (button.hasAttribute('download') || (button.target && button.target !== '_self')))) return;
    event.preventDefault();
    // Dawn binds its own click handler on the summary. Only the opted-in hook
    // stops propagation so its native modal cannot open behind this one.
    if (dawn || button.matches('summary')) event.stopPropagation();
    openAssistant(button, dawn ? 'search' : undefined);
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (event.defaultPrevented || !(form instanceof HTMLFormElement) || !form.matches('[data-dsp-open][data-dsp-mode="search"]')) return;
    event.preventDefault();
    openAssistant(form, 'search', event.submitter || form);
  });

  window.addEventListener('message', async event => {
    if (event.origin !== APP_ORIGIN) return;
    const sourceFrame = [...document.querySelectorAll('iframe[data-dsp-iframe]')].find(el => {
      try { return el.contentWindow === event.source && new URL(el.src).origin === APP_ORIGIN; }
      catch { return false; }
    });
    if (!sourceFrame || !event.data || typeof event.data !== 'object') return;
    if (event.data.type === 'dsp:input-focus') {
      if (sourceFrame === frame && typeof event.data.focused === 'boolean' && dialog?.open) {
        // A child blur must never overwrite the real storefront field's focus.
        updateInputFocus(event.data.focused || document.activeElement === hostSearchInput);
      }
      return;
    }
    if (event.data.type === 'dsp:navigation-capabilities') { event.source.postMessage({ type: 'dsp:navigation-capabilities', version: 1 }, APP_ORIGIN); return; }
    if (event.data.type === 'dsp:navigate-product') {
      const destination = productDestination(event.data.url);
      if (destination) window.location.assign(destination);
      return;
    }
    if (event.data.type === 'dsp:close') { if (sourceFrame === frame) dialog?.close(); return; }
    if (event.data.type === 'dsp:analytics-consent-request') { loadPrivacy(); sendAnalyticsConsent(sourceFrame); return; }
    if (event.data.type === 'dsp:ready') {
      event.source.postMessage({ type: 'dsp:navigation-capabilities', version: 1 }, APP_ORIGIN);
      loadPrivacy(); sendAnalyticsConsent(sourceFrame);
      if (sourceFrame === frame) {
        frameReady = true;
        window.clearTimeout(readyTimer);
        status.textContent = '';
        sendOpen();
      }
      if (sourceFrame.hasAttribute('data-dsp-inline')) {
        rootElement()?.classList.add('dsp-has-inline');
        readyInlineFrames.add(sourceFrame);
        sendInlineOpen(sourceFrame);
      }
      return;
    }
    if (event.data.type === 'dsp:mode-changed') {
      if (sourceFrame === frame && ['chat', 'search'].includes(event.data.mode)) setMode(event.data.mode);
      return;
    }
    if (['dsp:search-query', 'dsp:search-selection'].includes(event.data.type)) {
      if (typeof event.data.query !== 'string' || event.data.query.length > 300) return;
      const query = searchQuery(event.data.query);
      if (sourceFrame === frame) {
        const explicit = event.data.type === 'dsp:search-selection';
        if (dialog?.dataset.hostSearch !== 'true' || document.activeElement !== hostSearchInput || explicit) {
          lastPopupQuery = query;
          if (hostSearchInput && dialog?.dataset.hostSearch === 'true') {
            if (explicit && !query) hostSearchInput.blur();
            hostSearchInput.value = query;
            hostSearchForm.querySelector('.dsp-host-search-clear').hidden = !query;
          }
          updateFullPageLink();
        }
      }
      return;
    }
    if (event.data.type === 'dsp:cart-capabilities') { event.source.postMessage({ type: 'dsp:cart-capabilities', version: 2 }, APP_ORIGIN); return; }
    if (!['dsp:add-to-cart', 'dsp:check-cart'].includes(event.data.type)) return;
    const { requestId, items, mode = 'ensure' } = event.data;
    if (typeof requestId !== 'string' || !/^[a-f\d-]{36}$/i.test(requestId)) return;
    const reply = status => {
      completedRequests.set(requestId, status);
      if (completedRequests.size > 100) completedRequests.delete(completedRequests.keys().next().value);
      event.source?.postMessage({ type: 'dsp:cart-result', requestId, ok: status === 'success', status }, APP_ORIGIN);
    };
    if (event.data.type === 'dsp:check-cart') {
      if (cartBusy) { reply('unknown'); return; }
      if (!unresolvedCart) { reply(completedRequests.get(requestId) || 'failed'); return; }
      // A reloaded or second iframe must not confirm a different selection using this write's targets.
      if (requestId !== unresolvedCart.requestId) { reply('unknown'); return; }
      cartBusy = true;
      try {
        const cart = await readCart();
        if (unresolvedCart.targets.every(item => plainQuantity(cart, item.id) >= item.quantity)) {
          completedRequests.set(unresolvedCart.requestId, 'success');
          unresolvedCart = null;
          reply('success');
          await presentStoreCart(sourceFrame, requestId).catch(() => {});
        } else reply(unresolvedCart.targets.some(item => plainQuantity(cart, item.id) > item.before) ? 'partial' : 'unknown');
      } catch { reply('unknown'); }
      finally { cartBusy = false; }
      return;
    }
    if (cartBusy && activeRequestId === requestId) return;
    if (completedRequests.has(requestId)) { reply(completedRequests.get(requestId)); return; }
    if (unresolvedCart) { reply('unknown'); return; }
    if (cartBusy || !['add', 'ensure'].includes(mode) || !Array.isArray(items) || !items.length || items.length > 10 || items.some(item => !item || !/^\d{1,20}$/.test(item.id) || !Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > 99) || new Set(items.map(item => item.id)).size !== items.length) { reply('failed'); return; }
    cartBusy = true;
    activeRequestId = requestId;
    try {
      // Read the browser's real Shopify cart. Never clear or replace its lines.
      const existing = await readCart();
      const targets = items.map(item => {
        const before = plainQuantity(existing, item.id);
        return { id: item.id, before, quantity: mode === 'add' ? before + item.quantity : item.quantity };
      });
      const additions = targets.flatMap(item => {
        const quantity = Math.max(0, item.quantity - item.before);
        return quantity ? [{ id: item.id, quantity }] : [];
      });
      if (additions.length) {
        unresolvedCart = { requestId, targets };
        try {
          await fetch(`${rootPath()}cart/add.js`, {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: additions }), signal: AbortSignal.timeout(15000),
          });
        } catch { /* A lost response can still mean Shopify committed the add. Read back before deciding. */ }
      }
      const finalCart = await readCart();
      if (!targets.every(item => plainQuantity(finalCart, item.id) >= item.quantity)) {
        reply(targets.some(item => plainQuantity(finalCart, item.id) > item.before) ? 'partial' : 'unknown');
        return;
      }
      unresolvedCart = null;
      reply('success');
      await presentStoreCart(sourceFrame, requestId).catch(() => {});
    } catch { reply(unresolvedCart ? 'unknown' : 'failed'); }
    finally { cartBusy = false; activeRequestId = null; }
  });
  enhanceHeaderSearch();
  document.addEventListener('shopify:section:load', enhanceHeaderSearch);
  const existingInline = inlineFrame();
  if (existingInline) {
    rootElement()?.classList.add('dsp-has-inline');
    if (new URL(window.location.href).searchParams.get('dsp_mode') === 'chat') {
      pendingInlineOpens.set(existingInline, { type: 'dsp:open', mode: 'chat', productId: null, currentProductId: null });
    }
    // Its first ready event may have preceded this deferred theme script.
    requestReady(existingInline);
  }
})();
